/**
 * sun-hover-images.js — Web Component <sun-hover-images>
 *
 * Replaces the Broadcast <hover-images> tag on Sun product cards.
 * Desktop: animates --slider-index from 0 to 1 on mouseenter (RAF cubic ease).
 * Touch:   listens to scroll and writes --slider-index = scrollLeft / clientWidth.
 * The progress bar consumes --slider-index / --slides-count via CSS.
 *
 * Cross-cutting: rendered inside snippets/sun-product-grid-item.liquid (used
 * by every product card across the storefront), therefore loaded once globally
 * from snippets/head.liquid.
 */
if (!customElements.get('sun-hover-images')) {
  customElements.define('sun-hover-images', class extends HTMLElement {
    connectedCallback() {
      this.slider = this.querySelector('[data-hover-slider]');
      if (!this.slider) return;

      this.slides = this.slider.querySelectorAll('.product-item__bg__slide');
      if (this.slides.length < 2) return;

      const isTouch = window.theme && window.theme.touch;
      this.slidesCount = isTouch ? this.slides.length : 2;
      this.style.setProperty('--slides-count', this.slidesCount);
      this.style.setProperty('--slider-index', 0);

      if (isTouch) {
        this.initTouch();
      } else {
        this.initHover();
      }
    }

    disconnectedCallback() {
      if (this.onScroll && this.slider) {
        this.slider.removeEventListener('scroll', this.onScroll);
      }
      if (this.onEnter) this.removeEventListener('mouseenter', this.onEnter);
      if (this.onLeave) this.removeEventListener('mouseleave', this.onLeave);
      if (this.rafId) cancelAnimationFrame(this.rafId);
    }

    initTouch() {
      this.onScroll = () => {
        const index = this.slider.scrollLeft / this.slider.clientWidth;
        this.style.setProperty('--slider-index', index);
      };
      this.slider.addEventListener('scroll', this.onScroll, { passive: true });
    }

    initHover() {
      this.currentIndex = 0;
      this.duration = 600;
      this.onEnter = () => this.animateTo(1);
      this.onLeave = () => this.animateTo(0);
      this.addEventListener('mouseenter', this.onEnter);
      this.addEventListener('mouseleave', this.onLeave);
    }

    animateTo(target) {
      if (this.rafId) cancelAnimationFrame(this.rafId);
      const start = this.currentIndex;
      const delta = target - start;
      if (delta === 0) return;

      const startTime = performance.now();
      const ease = (t) => 1 - Math.pow(1 - t, 3);

      const tick = (now) => {
        const progress = Math.min((now - startTime) / this.duration, 1);
        const value = start + delta * ease(progress);
        this.currentIndex = value;
        this.style.setProperty('--slider-index', value);
        if (progress < 1) {
          this.rafId = requestAnimationFrame(tick);
        } else {
          this.rafId = null;
          this.currentIndex = target;
        }
      };
      this.rafId = requestAnimationFrame(tick);
    }
  });
}
